/**
 * La anatomia de una tarifa aerea, en pantalla.
 *
 * ===========================================================================
 * UNA TARIFA AEREA NO SE LEE COMO UNA MARITIMA
 *
 * En FCL la pregunta es "cuanto cuesta esta caja de aqui a alla". El total por
 * contenedor la contesta entera, y lo demas --transito, dias libres, vigencia--
 * son matices que se comparan en columnas.
 *
 * En aereo el total NO contesta nada por si solo, porque el numero de kilos que
 * se cobran no es el que marca la bascula y el precio del kilo depende de ese
 * numero. Un agente que ve "USD 4.450" y una carga de 300 kg no puede decirle
 * nada al cliente: le falta saber que se cobran 500, que el kilo va a 8,90 y
 * que los 500 salen del volumen y no del peso.
 *
 * Por eso esta pieza existe y por eso pone DELANTE cuatro cosas que en maritimo
 * ni siquiera hay:
 *
 *   1. EL PRECIO POR KILO del escalon aplicado. Es la cifra que el agente
 *      compara entre tarifas y la que el proveedor le va a decir por telefono.
 *   2. EL PESO TARIFABLE, con su explicacion al lado --"cobra 500 kg: el
 *      volumetrico da 333"--. La explicacion no es un adorno: es lo que evita la
 *      llamada del cliente preguntando por que le facturan mas de lo que pesa.
 *   3. LA RUTA CON SUS ESCALAS, como elemento central y no como un dato del
 *      detalle. Un vuelo con dos escalas y uno directo no son el mismo producto
 *      aunque el transito declarado sea parecido.
 *   4. LAS ADVERTENCIAS OPERATIVAS, imposibles de no ver. Un embargo hace que la
 *      tarifa no se pueda reservar, y eso no lo dice el precio.
 *
 * ---------------------------------------------------------------------------
 * EL PRECIO POR KILO PUEDE NO VENIR, Y LOS KILOS SIEMPRE VIENEN
 *
 * `ve_costo` se lleva `precio_por_kg` y nada mas de este bloque. Los kilos no
 * son dinero: un agente que no ve costos necesita igual poder explicar por que
 * a una carga de 300 kg le cobran 500, porque esa conversacion la tiene el, no
 * quien ve los costos. Ver CLAUDE.md, "La regla es no mandarlos, no taparlos":
 * aqui no se pregunta `ve_costo` para decidir que se esconde, se pinta lo que
 * llego.
 * ---------------------------------------------------------------------------
 */

import type {
  AptitudBulto,
  BloqueAereo,
  EscalaAerea,
  IncumplimientoBulto,
  PuntoAereo,
  ResultadoTarifa,
} from '../api';
import { dato, el, literal } from '../dom';
import { formatearMoneda, type Idioma } from '../formato';
import type { Traductor } from '../i18n';

/**
 * Los dias de la semana en orden ISO, para traducir la mascara. [H84]
 *
 * La API manda numeros --1 lunes ... 7 domingo-- y no nombres, porque los
 * nombres son etiquetas y las etiquetas viven aqui [CLAUDE.md, Idioma]. "D357"
 * llega como [3, 5, 7] y sale como "Mi · Vi · Do".
 */
const DIAS_ISO = [1, 2, 3, 4, 5, 6, 7] as const;

/**
 * El precio por kilo, que es la cifra que se compara.
 *
 * Devuelve `null` cuando no vino --sin `ve_costo`-- y quien llama decide que
 * pone en su lugar. NUNCA un cero ni una celda vacia: un precio ausente y un
 * precio de cero son cosas distintas [H59].
 */
export function precioPorKg(
  a: BloqueAereo,
  moneda: string,
  idioma: Idioma,
): string | null {
  const p = a.escalon.precio_por_kg;
  if (p === undefined) return null;
  return formatearMoneda(p, moneda, idioma);
}

/**
 * La celda de precio por kilo: la cifra grande y el escalon que la eligio.
 *
 * El escalon va pegado al precio y no en el detalle porque son el mismo dato
 * partido en dos: "8,90/kg" sin "+300" no dice a que pesos aplica, y a otro
 * peso el mismo proveedor cobra otra cosa [H8][Invariante 12].
 */
export function celdaPorKg(
  r: ResultadoTarifa,
  a: BloqueAereo,
  t: Traductor,
  idioma: Idioma,
): HTMLElement {
  const moneda = r.total?.moneda ?? r.cargos[0]?.moneda ?? '';
  const precio = precioPorKg(a, moneda, idioma);

  return el('div', { clase: 'aereo-kg' }, [
    precio === null
      ? // Se dice con palabras por que falta, en el sitio donde estaria la
        // cifra. Un guion aqui se leeria como "la tarifa no lo declara".
        el('span', { clase: 'tenue', texto: t('aereo.sinPrecioPorKg') })
      : el('span', { clase: 'aereo-kg-monto' }, [
          el('span', { clase: 'cifra', texto: precio }),
          el('span', { clase: 'aereo-kg-unidad', texto: t('aereo.porKg') }),
        ]),
    el('span', {
      clase: 'aereo-escalon',
      texto: t('aereo.escalon', { etiqueta: a.escalon.etiqueta }),
      attrs: { title: rangoEscalon(a, t) },
    }),
  ]);
}

/** "de 300 a 500 kg", "1000 kg o mas". El escalon es semiabierto: 500 justos
 *  pagan el siguiente [migracion 0023]. */
function rangoEscalon(a: BloqueAereo, t: Traductor): string {
  const { desde_kg: desde, hasta_kg: hasta } = a.escalon;
  if (desde !== null && hasta !== null) {
    return t('aereo.rangoEntre', { desde: kg(desde), hasta: kg(hasta) });
  }
  if (desde !== null) return t('aereo.rangoDesde', { desde: kg(desde) });
  if (hasta !== null) return t('aereo.rangoHasta', { hasta: kg(hasta) });
  return t('aereo.rangoSinCotas');
}

/** Kilos sin ceros de relleno: "333.333" se lee, "333.333000" no. */
function kg(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : v;
}

/**
 * El peso tarifable, con la frase que lo explica.
 *
 * LA EXPLICACION ES EL PUNTO. "Cobra 500 kg: el volumetrico da 333" contesta de
 * una vez las dos preguntas que llegan siempre --cuantos kilos y por que ese
 * numero-- y las contesta ANTES de que el cliente pregunte. Sin ella el bloque
 * seria tres cifras sueltas que hay que saber interpretar.
 *
 * Los tres casos se dicen distinto porque son distintos:
 *
 *   VOLUMETRICO  se cobra mas de lo que pesa. Es el caso que sorprende.
 *   REAL         se cobra la bascula. El volumetrico se enseña igual, porque
 *                saber que NO mando es lo que confirma que el calculo se hizo.
 *   EMPATE       los dos dan lo mismo. Existe --300 kg en 1,8 m3 con la relacion
 *                de IATA-- y meterlo en cualquiera de los otros dos haria que la
 *                explicacion afirmara algo que no paso.
 */
export function pesoTarifable(a: BloqueAereo, t: Traductor): HTMLElement {
  const { real_kg: real, volumetrico_kg: volumetrico, tarifable_kg: tarifable } = a.peso;

  const explicacion = t(`aereo.manda.${a.peso.manda}`, {
    real: kg(real),
    volumetrico: kg(volumetrico),
  });

  return el('div', { clase: 'aereo-peso' }, [
    el('div', { clase: 'aereo-peso-cifra' }, [
      el('span', { clase: 'cifra', texto: kg(tarifable) }),
      el('span', { clase: 'aereo-peso-unidad', texto: t('aereo.kg') }),
      a.peso.manda === 'VOLUMETRICO'
        ? el('span', { clase: 'badge badge-atencion', texto: t('aereo.volumetrico') })
        : null,
    ]),
    // En texto, siempre visible, nunca en un `title`. Un aviso que hay que
    // descubrir con el raton no existe en una pantalla que se usa con prisa.
    el('p', { clase: 'aereo-peso-explica', texto: explicacion }),
    // Con que relacion se convirtio. El literal del proveedor cuando lo hay
    // --"1:500"-- porque es lo que el agente reconoce del correo.
    a.peso.relacion_volumetrica === null
      ? null
      : el('p', { clase: 'tenue aereo-relacion' }, [
          el('span', {
            texto: t('aereo.relacion', { valor: a.peso.relacion_volumetrica }),
          }),
          literal(a.peso.relacion_volumetrica_literal),
        ]),
  ]);
}

/**
 * La ruta con sus escalas, como cadena de nodos.
 *
 * ELEMENTO CENTRAL, no un dato del detalle. Un SZX -> HKG -> LAX -> NLU y un
 * directo no son el mismo producto: cada escala es una descarga, una espera y
 * una oportunidad de que la carga se quede en tierra.
 *
 * Tres cosas que se marcan y no se esconden:
 *
 *   EL PRE-ACARREO. Un tramo TERRESTRE lleva su propia marca: la carga sale del
 *   aeropuerto declarado por carretera y despega de otro [H82]. El transito ya
 *   lo cuenta, pero quien coordina la recogida necesita verlo.
 *
 *   LAS ALTERNATIVAS. "LAX/JFK/NLU" es un nodo que el proveedor dejo abierto y
 *   que la ingesta resolvio al primero por regla mecanica. Se marca, porque
 *   pintar solo "LAX" afirmaria una escala que el correo no afirmaba [H12].
 *
 *   EL LITERAL DEL ROUTING. Debajo, sin traducir. Es contra lo que el agente
 *   compara cuando abre el correo.
 */
export function ruta(a: BloqueAereo, t: Traductor): HTMLElement {
  const escalas = a.ruta.escalas;

  return el('div', { clase: 'aereo-ruta' }, [
    escalas.length === 0
      ? // Sin itinerario cargado no se inventa uno con el origen y el destino:
        // eso afirmaria un vuelo directo que nadie declaro. Se dice.
        el('p', { clase: 'tenue', texto: t('aereo.sinItinerario') })
      : el(
          'ol',
          { clase: 'aereo-escalas' },
          escalas.flatMap((e, i) => [
            // El origen se pinta cuando ABRE la cadena o cuando NO ES el
            // destino del tramo anterior. Lo normal es lo primero --los tramos
            // vienen encadenados-- y entonces el nodo compartido se pinta una
            // sola vez: SZX -> HKG -> LAX y no SZX -> HKG, HKG -> LAX.
            //
            // Lo segundo es un itinerario con un hueco, que no deberia llegar y
            // que si llega NO se disimula: encadenarlo igual dibujaria un vuelo
            // directo entre dos puntos que la tarifa no une, y eso es afirmar
            // una ruta que nadie declaro [H12].
            i === 0 || escalas[i - 1].destino.iata !== e.origen.iata
              ? nodoRuta(e.origen, t)
              : null,
            saltoRuta(e, t),
            nodoRuta(e.destino, t),
          ]),
        ),
    a.ruta.aeropuerto_salida === null
      ? null
      : // [H82] Se recoge en un sitio y se despega de otro. Va aparte del
        // itinerario y en un aviso: no es una escala mas, es que el punto de
        // recogida no es el punto de salida.
        el('div', { clase: 'aviso aereo-preacarreo' }, [
          el('p', {
            texto: t('aereo.preacarreo', {
              salida: nombrePunto(a.ruta.aeropuerto_salida),
            }),
          }),
        ]),
    a.ruta.literal === null
      ? null
      : el('p', { clase: 'aereo-ruta-literal' }, [literal(a.ruta.literal)]),
  ]);
}

function nombrePunto(p: PuntoAereo): string {
  return p.iata ?? p.nombre;
}

function nodoRuta(p: PuntoAereo, t: Traductor): HTMLElement {
  return el('li', { clase: 'aereo-nodo' }, [
    el('code', { clase: 'aereo-nodo-codigo', texto: nombrePunto(p) }),
    // El nombre completo al lado del designador. "NLU" no le dice nada a quien
    // no lleva años, y el `title` seria otro dato escondido.
    p.iata === null ? null : el('span', { clase: 'aereo-nodo-nombre', texto: p.nombre }),
    el('span', { clase: 'visualmente-oculto', texto: t('aereo.escala') }),
  ]);
}

/** La flecha entre dos nodos, con lo que la tarifa dice de ese salto. */
function saltoRuta(e: EscalaAerea, t: Traductor): HTMLElement {
  const terrestre = e.modo === 'TERRESTRE';
  return el('li', { clase: `aereo-salto${terrestre ? ' aereo-salto-terrestre' : ''}` }, [
    el('span', { clase: 'aereo-flecha', attrs: { 'aria-hidden': 'true' }, texto: '→' }),
    terrestre
      ? el('span', { clase: 'badge badge-atencion', texto: t('aereo.trucking') })
      : null,
    literal(e.carrier_literal),
    e.alternativas === null
      ? null
      : el('span', { clase: 'aviso-linea' }, [
          el('span', { texto: t('aereo.alternativas') }),
          literal(e.alternativas),
        ]),
  ]);
}

/**
 * La frecuencia, interpretada. [H84]
 *
 * "D357" es una convencion del sector y no se lee sola. La mascara ya viene
 * resuelta de la API en dias ISO y aqui solo se traducen los nombres; el
 * literal se conserva al lado, porque la interpretacion puede estar mal y el
 * literal no [CLAUDE.md, Literales].
 *
 * `dias` nula significa QUE NO SE INTERPRETO, nunca "no opera": ahi manda el
 * literal, y si tampoco lo hay se dice que no se declaro.
 */
export function frecuencia(a: BloqueAereo, t: Traductor): HTMLElement {
  const dias = a.frecuencia.dias;

  return el('div', { clase: 'aereo-frecuencia' }, [
    dias === null || dias.length === 0
      ? el('span', {
          clase: 'tenue',
          texto:
            a.frecuencia.literal === null
              ? t('resultado.noDeclarado')
              : t('aereo.frecuenciaSinInterpretar'),
        })
      : el(
          'span',
          { clase: 'aereo-dias' },
          DIAS_ISO.map((d) =>
            el('span', {
              // Los siete siempre, con los que no opera apagados: una lista de
              // tres dias sueltos obliga a reconstruir la semana de memoria, y
              // "Ma · Ju · Sa" no dice si el miercoles opera o no se sabe.
              clase: `aereo-dia${dias.includes(d) ? ' aereo-dia-opera' : ''}`,
              texto: t(`dia.corto.${String(d)}`),
              attrs: {
                title: `${t(`dia.largo.${String(d)}`)}: ${
                  dias.includes(d) ? t('aereo.opera') : t('aereo.noOpera')
                }`,
              },
            }),
          ),
        ),
    literal(a.frecuencia.literal),
  ]);
}

/**
 * Los limites por bulto y el veredicto contra la carga declarada. [H85]
 *
 * NO_APTA NO ESCONDE LA TARIFA. La marca, y dice por cuanto falla. Es la misma
 * regla que gobierna todo el buscador: lo que no aparece no se puede revisar
 * [CLAUDE.md, "No filtra tarifas, y es deliberado"]. Un agente que ve "el bulto
 * mide 2,6 m y el maximo es 2,4" puede llamar al proveedor; uno que no ve la
 * tarifa, no.
 *
 * NO_COMPROBABLE se pinta y no se calla. "No se pudo comprobar" y "cabe" son
 * cosas distintas, y un bloque que solo apareciera en el caso malo las
 * confundiria: la ausencia de aviso se leeria como aprobacion.
 */
export function restriccionBulto(a: BloqueAereo, t: Traductor): HTMLElement | null {
  const r = a.restriccion_bulto;
  if (r === null && a.aptitud.resultado === 'NO_COMPROBABLE') return null;

  const medidas = [
    [t('aereo.maxLargo'), r?.max_largo_m],
    [t('aereo.maxAncho'), r?.max_ancho_m],
    [t('aereo.maxAlto'), r?.max_alto_m],
    [t('aereo.maxPesoPieza'), r?.max_peso_pieza_kg],
  ] as const;

  return el('div', { clase: 'aereo-bulto' }, [
    veredictoAptitud(a.aptitud, t),
    el(
      'div',
      { clase: 'detalle-datos' },
      medidas.map(([etiqueta, valor]) =>
        valor == null ? null : dato(etiqueta, kg(valor)),
      ),
    ),
    // El texto del proveedor, sin traducir. Es lo unico que hay cuando la
    // ingesta no supo sacar numeros de la frase.
    r?.literal == null ? null : literal(r.literal),
  ]);
}

function veredictoAptitud(a: AptitudBulto, t: Traductor): HTMLElement {
  if (a.resultado === 'NO_APTA') {
    return el('div', { clase: 'aviso aviso-critico aereo-no-apta' }, [
      el('strong', { texto: t('aereo.noApta') }),
      ...(a.incumplimientos ?? []).map((i) => el('p', { texto: frase(i, t) })),
    ]);
  }
  if (a.resultado === 'APTA') {
    return el('p', { clase: 'aereo-apta', texto: t('aereo.apta') });
  }
  // NO_COMPROBABLE. Se dice POR QUE no hay veredicto: sin eso, el agente no
  // sabe si le falta declarar los bultos o si la tarifa no declara limites.
  return el('p', { clase: 'tenue', texto: t('aereo.noComprobable') });
}

/** "El bulto mide 2,6 m y el maximo de esta tarifa es 2,4 m." Con los dos
 *  numeros: el margen por el que falla decide si vale la pena preguntar. */
function frase(i: IncumplimientoBulto, t: Traductor): string {
  return t(`aereo.incumple.${i.motivo}`, {
    declarado: kg(i.declarado),
    limite: kg(i.limite),
  });
}
